const currency = require('currency.js');

// markupPrice preferred over base price: analytics needs what the customer is charged,
// not the agency-facing rate the UI may show (checkDoShowMarkupRates is display-only).
const getHotelsAmount = (hotelItineraries) => {
  const itineraries = Object.values(hotelItineraries || {});
  if (!itineraries.length) return undefined;

  return itineraries.reduce((total, itinerary) => {
    const selectedProduct = itinerary.hotel?.products?.find(
      (product) => product.code === itinerary.selectedRoomCode,
    );
    const roomPrice = selectedProduct?.roomPrice;

    return total + (roomPrice?.markupPrice ?? roomPrice?.originalPrice ?? 0);
  }, 0);
};

const getToursAmount = (tourItineraries) => {
  const itineraries = tourItineraries || [];
  if (!itineraries.length) return undefined;

  return itineraries.reduce((total, itinerary) => total + (itinerary.selectedSlot?.price ?? 0), 0);
};

const getCarsAmount = (rentalCarItineraries) => {
  const itineraries = Object.values(rentalCarItineraries || {});
  if (!itineraries.length) return undefined;

  return itineraries.reduce(
    (total, itinerary) => total + (itinerary.cost?.markupPrice ?? itinerary.cost?.price ?? 0),
    0,
  );
};

const getInsuranceAmount = (insuranceItinerary) => {
  const quote = insuranceItinerary?.insuranceInfo?.quote;
  if (typeof quote?.totalCost !== 'number') return undefined;

  const upgradesCost = (insuranceItinerary.insuranceInfo.upgrades || []).reduce(
    (total, upgrade) => total + (upgrade.price || 0),
    0,
  );

  return quote.totalCost + upgradesCost;
};

const getCustomInventoryAmount = (customInventoryItinerary) => {
  const selectedProduct = customInventoryItinerary?.products?.find(
    (product) => product.id === customInventoryItinerary.selectedProductId,
  );

  return selectedProduct?.pricing?.markupPrice;
};

// undefined means "no data" — a segment where no item carries commission stays
// undefined so analytics omits the property instead of reporting a fake 0.
const sumDefined = (values) => {
  const defined = values.filter((value) => typeof value === 'number');
  if (!defined.length) return undefined;

  return defined.reduce((total, value) => total + value, 0);
};

const getHotelsCommission = (hotelItineraries) =>
  sumDefined(
    Object.values(hotelItineraries || {}).map((itinerary) => {
      const selectedProduct = itinerary.hotel?.products?.find(
        (product) => product.code === itinerary.selectedRoomCode,
      );

      return selectedProduct?.roomPrice?.agentCommission;
    }),
  );

const getCarsCommission = (rentalCarItineraries) =>
  sumDefined(
    Object.values(rentalCarItineraries || {}).map(
      (itinerary) => itinerary.priceInfo?.agentCommission,
    ),
  );

const round = (amount) => (typeof amount === 'number' ? currency(amount).value : undefined);

// Cruise rates have no currency field and are USD by convention
// (see getTripConfigurationItems), so USD is the fallback.
const getCurrencyCode = (trip) => {
  const selectedHotelProduct = Object.values(trip.hotelItineraries || {})
    .map((itinerary) =>
      itinerary.hotel?.products?.find((product) => product.code === itinerary.selectedRoomCode),
    )
    .find(Boolean);

  return (
    trip.flightItinerary?.cost?.currencyCode ||
    selectedHotelProduct?.roomPrice?.currencyCode ||
    Object.values(trip.rentalCarItineraries || {}).find((itinerary) => itinerary.cost?.currencyCode)
      ?.cost?.currencyCode ||
    trip.insuranceItinerary?.insuranceInfo?.quote?.currency ||
    (trip.tourItineraries || [])
      .map((itinerary) => itinerary.selectedSlot?.currencyCode || itinerary.cost?.currencyCode)
      .find(Boolean) ||
    'USD'
  );
};

const computeTripSegmentAmounts = ({ trip, cruiseTotals }) => {
  const amounts = {
    cruiseAmount: trip.cruiseItinerary ? round(cruiseTotals?.totalAmount) : undefined,
    flightAmount: round(trip.flightItinerary?.cost?.price),
    hotelAmount: round(getHotelsAmount(trip.hotelItineraries)),
    carAmount: round(getCarsAmount(trip.rentalCarItineraries)),
    tourAmount: round(getToursAmount(trip.tourItineraries)),
    insuranceAmount: round(getInsuranceAmount(trip.insuranceItinerary)),
    customInventoryAmount: round(getCustomInventoryAmount(trip.customInventoryItinerary)),
  };

  const definedAmounts = Object.values(amounts).filter((amount) => typeof amount === 'number');
  const bookingAmount = definedAmounts.length
    ? currency(definedAmounts.reduce((total, amount) => total + amount, 0)).value
    : undefined;

  // Commission mirrors what the UI price breakdown shows per segment
  // (see getTotalPriceBreakdown) — same trip-state sources, measurement-only.
  const commissions = {
    cruiseCommissionAmount: trip.cruiseItinerary
      ? round(cruiseTotals?.commission?.amount)
      : undefined,
    flightCommissionAmount: round(trip.flightItinerary?.agentCommission),
    hotelCommissionAmount: round(getHotelsCommission(trip.hotelItineraries)),
    carCommissionAmount: round(getCarsCommission(trip.rentalCarItineraries)),
  };

  const definedCommissions = Object.values(commissions).filter(
    (amount) => typeof amount === 'number',
  );
  const commissionAmount = definedCommissions.length
    ? currency(definedCommissions.reduce((total, amount) => total + amount, 0)).value
    : undefined;

  return {
    ...amounts,
    bookingAmount,
    ...commissions,
    commissionAmount,
    currencyCode: getCurrencyCode(trip),
  };
};

export default computeTripSegmentAmounts;
